/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  IconBuilding,
  IconBuildingStore,
  IconBuildingWarehouse,
  IconCalendarClock,
  IconChecks,
  IconCloudDown,
  IconContract,
  IconCopy,
  IconEdit,
  IconHome2,
  IconId,
  IconMail,
  IconUser,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react";

import {
  useStaffDetailsData,
  useStaffUpdateMutation,
} from "../../../apis/staffApis";

import AddStaffModal from "./AddStaffModal";
import { useForm } from "react-hook-form";
import formatDate from "../../../utilities/helper";
import toast from "react-hot-toast";
import { usePermission } from "../../../hooks/usePermissionTabs";
import NoDataFound from "../../../components/common/NoDataFound";
import NormalSearch from "../../../components/common/NormalSearch";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  staffId: string;
  open: boolean;
  onClose: () => void;
};

const Row = ({
  icon,
  label,
  value,
  icon2,
  className,
}: {
  icon?: React.ReactNode;
  icon2?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="flex items-center gap-2">
        {icon && <div className="mt-0.5 text-gray-500">{icon}</div>}
        <span className="font-medium text-sm text-[#212121]">{t(label)}: </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="break-all text-xs">{value}</span>
        {icon2 && <div className="mt-0.5 text-gray-500">{icon2}</div>}
      </div>
    </div>
  );
};

// Tab Components
type TabType = "personal" | "warehouse" | "permissions";

export const TabButton = ({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}) => {
  const { t } = useLanguage();
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
        active
          ? "bg-[#FBD037]/20  text-[#000000] shadow-sm border"
          : "bg-slate-100 text-slate-600 border"
      }`}
    >
      {icon}
      {t(label)}
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

export default function ViewStaffModal({ staffId, open, onClose }: Props) {
  const { t } = useLanguage();
  const {
    data: staffDetails,
    refetch,
    isFetching,
  } = useStaffDetailsData(staffId);
  const [copied, setCopied] = useState(false);
  const { mutate, isPending } = useStaffUpdateMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialog, setEditDialog] = useState(false);
  const [allIds, setAllIds] = useState<any>();
  const [selectedDocument, setSelectedDocument] = useState<any>({
    photo: false,
    iqamaDoc: false,
  });
  const [activeTab, setActiveTab] = useState<TabType>("personal");

  const { allowed: canUpdateStaff } = usePermission("staffs", "update");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    if (open && staffId) {
      refetch();
    }
  }, [open, staffId]);

  const handleUpdate = (data: any) => {
    const mergedWarehouses: string[] = [];

    if (data.warehouse) mergedWarehouses.push(data.warehouse);
    if (Array.isArray(data.assignWarehouse)) {
      mergedWarehouses.push(...data.assignWarehouse.map((w: any) => w?.id));
    } else if (data.assignWarehouse) {
      mergedWarehouses.push(data.assignWarehouse);
    }

    let { photo, iqamaDoc, ...payload } = data;

    mutate(
      {
        ...payload,
        ...(selectedDocument?.photo && { photo }),
        ...(selectedDocument?.iqamaDoc && { iqamaDoc }),
        ...allIds,
        staffRolesId: data.roles,
        warehouses: mergedWarehouses,
        type: "LM",
        id: staffId,
      },
      {
        onSuccess: () => {
          toast.success(t("Staff Updated Successfully"));
          setEditDialog(false);
          reset();
          refetch();
        },
        onError: (err: any) => {
          toast.error(t(err?.message || "Update failed"));
        },
      },
    );
  };

  const handleCopy = async (text?: string) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const filteredPermissions =
    staffDetails?.permissions?.filter((perm: any) =>
      perm?.dashboard?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  if (!open) return null;

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden">
      {/* Modal Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 rounded-xl overflow-hidden">
            <img
              src={staffDetails?.photo || "/img/dummy-blank-profile.png"}
              alt={t("Staff Profile")}
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#212121]">
              {staffDetails?.name}
            </h2>
            {(staffDetails?.staffCode || staffDetails?.code) && (
              <p className="text-sm text-[#212121]/70">
                {staffDetails.staffCode || staffDetails.code}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!editDialog && canUpdateStaff && (
            <button
              onClick={() => {
                reset();
                setEditDialog(true);
              }}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
              title={t("Edit Staff")}
            >
              <IconEdit size={18} />
            </button>
          )}
          {editDialog && (
            <button
              onClick={handleSubmit(handleUpdate)}
              disabled={isPending}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? t("Updating...") : t("Update")}
            </button>
          )}
          <button
            onClick={() => {
              setEditDialog(false);
              reset();
              onClose();
            }}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="flex-1 overflow-y-auto">
        {isFetching ? (
          <div className="w-full space-y-4 p-10 text-center">
            <div className="animate-pulse">{t("Loading...")}</div>
          </div>
        ) : editDialog ? (
          <div className="py-2">
            <AddStaffModal
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              getIds={setAllIds}
              setSelectedDocument={setSelectedDocument}
              staffDetails={staffDetails}
            />
          </div>
        ) : (
          <div className="relative p-6  ">
            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200/50 pb-3">
              <TabButton
                active={activeTab === "personal"}
                onClick={() => setActiveTab("personal")}
                icon={<IconUser size={14} />}
                label="Personal Info"
              />
              <TabButton
                active={activeTab === "warehouse"}
                onClick={() => setActiveTab("warehouse")}
                icon={<IconBuildingWarehouse size={14} />}
                label="Warehouse"
              />
              <TabButton
                active={activeTab === "permissions"}
                onClick={() => setActiveTab("permissions")}
                icon={<IconUserCheck size={14} />}
                label="Roles"
                badge={staffDetails?.permissions?.length}
              />
            </div>

            {/* Personal Info Tab */}
            {activeTab === "personal" && (
              <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader icon={IconUser} title="Personal Info" />

                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="mt-0.5 text-gray-500">
                        <IconId size={18} />
                      </div>

                      <span className="font-medium text-sm text-[#212121]">
                        {t("Iqama No")} :{" "}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="break-all text-xs">
                        {staffDetails?.iqamaNo}
                      </span>

                      <div className="mt-0.5 text-gray-500">
                        {staffDetails?.iqamaDoc && (
                          <a
                            onClick={() =>
                              window.open(staffDetails?.iqamaDoc, "_blank")
                            }
                            className="cursor-pointer"
                          >
                            <IconCloudDown size={18} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="mt-0.5 text-gray-500">
                        <IconMail size={18} />
                      </div>

                      <span className="font-medium text-sm text-[#212121]">
                        {t("Email")} :{" "}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="break-all text-xs">
                        {staffDetails?.email}
                      </span>

                      <div
                        onClick={() => handleCopy(staffDetails?.email)}
                        className="cursor-pointer relative"
                      >
                        {copied ? (
                          <span className="text-xs text-green-600 font-medium">
                            <IconChecks size={18} />
                          </span>
                        ) : (
                          <IconCopy
                            size={18}
                            className="mt-0.5 text-gray-500"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <Row
                    icon={<IconContract size={18} />}
                    label="Joined"
                    value={formatDate(staffDetails?.startDate)}
                  />
                  <Row
                    icon={<IconHome2 size={18} />}
                    label="Supervisor"
                    value={staffDetails?.supervisorName}
                  />
                  <Row
                    icon={<IconCalendarClock size={18} />}
                    label="Time Slot"
                    value={t(staffDetails?.timeSlotName)}
                  />
                </div>
              </div>
            )}

            {/* Warehouse & Assigned Warehouse Tab (Combined) */}
            {activeTab === "warehouse" && (
              <div className="space-y-6">
                {/* Primary Warehouse Section */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader
                    icon={IconBuildingWarehouse}
                    title="Primary Warehouse"
                  />

                  <div className="space-y-3">
                    <Row
                      icon={<IconBuildingStore size={18} />}
                      label="Outlet"
                      value={staffDetails?.outletList?.[0]?.name || "-"}
                    />
                    <Row
                      icon={<IconBuildingWarehouse size={18} />}
                      label="Warehouse"
                      value={staffDetails?.warehouseList?.[0]?.name || "-"}
                    />
                  </div>
                </div>

                {/* Assigned Warehouse Section */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader
                    icon={IconBuilding}
                    title="Assigned Warehouse"
                  />

                  {staffDetails?.warehouseList?.slice(1)?.length > 0 ? (
                    <div className="space-y-2">
                      {staffDetails?.warehouseList
                        ?.slice(1)
                        ?.map((warehouse: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
                          >
                            <IconBuildingWarehouse
                              size={16}
                              className="text-gray-400"
                            />
                            <span className="text-gray-700 font-medium">
                              {warehouse.name}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400 flex flex-col items-center gap-2">
                      <IconBuildingWarehouse size={32} className="opacity-30" />
                      <p>{t("No additional warehouses assigned")}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Role Permissions Tab (Integrated Modal) */}
            {activeTab === "permissions" && (
              <div className="space-y-4">
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-[#212121] text-lg flex items-center gap-2">
                      <IconUserCheck size={20} className="text-purple-600" />
                      {t("Role Permissions")}
                      <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-600 rounded-full">
                        {filteredPermissions.length}
                      </span>
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t("Role")}: {t(staffDetails?.roleName)}
                    </p>
                  </div>
                  <NormalSearch
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  <div className="max-h-[590px] overflow-y-auto hide-scrollbar">
                    {filteredPermissions.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {filteredPermissions.map(
                          (permission: any, index: number) => (
                            <div
                              key={index}
                              className="p-4 hover:bg-gray-50/50 transition-colors"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <span className="h-7 w-7 flex-shrink-0 flex justify-center items-center text-[#212121]/70 rounded-full border border-gray-200 text-xs font-medium">
                                    {index + 1}
                                  </span>
                                  <p className="text-[#212121] text-sm font-semibold">
                                    {t(permission?.dashboard?.replace(/_/g, " ")?.toLowerCase())}
                                  </p>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {permission?.operations?.map(
                                    (op: string, idx: number) => {
                                      const operationColors: Record<
                                        string,
                                        string
                                      > = {
                                        create: "bg-green-100 text-green-700",
                                        read: "bg-blue-100 text-blue-700",
                                        update: "bg-orange-100 text-orange-700",
                                        delete: "bg-red-100 text-red-700",
                                      };
                                      const colorClass =
                                        operationColors[op.toLowerCase()] ||
                                        "bg-gray-100 text-gray-600";

                                      return (
                                        <span
                                          key={idx}
                                          className={`inline-block px-2 py-0.5 text-xs rounded-md font-medium ${colorClass}`}
                                        >
                                          {t(op.toLowerCase())}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="py-12">
                        <NoDataFound
                          mainClassName=""
                          subClassName=""
                          title={t("No Permissions Found")}
                          message={
                            searchTerm
                              ? t("Try a different search term")
                              : t("No permissions assigned to this role")
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
